import { Mutation } from '@apollo/client/react/components';
import format from 'date-fns/format';
import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { withRouter } from 'react-router-dom';

import {
  CircularProgress,
  Grid,
  IconButton,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
  withStyles,
} from '@material-ui/core';
import { NotificationImportant, Star } from '@material-ui/icons';

import { isIntervalActive } from '../../common/date-util';
import { AdminPolicy, useCheckPolicy } from '../policies';
import ProjectNotificationsWidget, {
  aggregateNotifications,
} from '../project/dashboard/project-notifications-widget';
import {
  FAVOURITE_PROJECT,
  GET_FAV_PROJECTS,
  GET_PROJECTS,
  UNFAVOURITE_PROJECT,
} from './home';

const styles = (theme) => ({
  htmlPopper: {},
  htmlTooltip: {
    backgroundColor: 'transparent',
    maxWidth: 500,
    fontSize: theme.typography.pxToRem(12),
    '& b': {
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
  1: {
    color: theme.palette.error.dark,
  },
  2: {
    color: '#e4a006',
  },
  3: {
    color: theme.palette.success.dark,
  },
  iconRow: {
    display: 'inline-block',
  },
});
const Secondary = ({ county, loading, startDate, endDate, customer }) => {
  return (
    <Grid container direction='row' justifyContent='space-between'>
      <Typography color='textSecondary'>
        {loading ? (
          <Skeleton width={200} />
        ) : (
          `${customer && customer.name}, ${county}`
        )}
      </Typography>

      <Typography color='textSecondary'>
        {loading ? (
          <Skeleton width={155} />
        ) : (
          `${format(new Date(startDate), 'yyyy-MM-dd')} — ${format(
            new Date(endDate),
            'yyyy-MM-dd'
          )}`
        )}
      </Typography>
    </Grid>
  );
};

const ProjectListItem = ({
  project,
  clickCallback,
  history,
  loading = false,
  data,
  selected,
  buttonRef,
  classes,
}) => {
  const isAdminOrHigher = useCheckPolicy(AdminPolicy);
  const { id, name, projectKey, isFavourite } = project;
  if (data && data.project && data.project.measuringCards) {
    data.project.measuringCards.forEach((c) => ({
      ...c,
      isDormant: !isIntervalActive(new Date(c.validFrom), new Date(c.validTo)),
    }));
  }
  let warnings =
    isAdminOrHigher && !loading && data ? aggregateNotifications(data) : [];

  const companyKey = useMemo(() => projectKey?.split('-')[0], [projectKey]);

  const projectReference = useMemo(
    () => projectKey?.split('-')[1],
    [projectKey]
  );

  return (
    <ListItem
      selected={selected}
      button
      divider
      ref={buttonRef}
      onClick={() => {
        if (clickCallback) {
          clickCallback();
        }
        history.push(
          `/project/${projectKey}${isAdminOrHigher ? '' : '/measuringdata'}`
        );
      }}
    >
      <ListItemText
        style={{ maxWidth: 50 }}
        disableTypography
        primary={
          <Typography variant='h6' color='textSecondary'>
            {projectReference || <Skeleton width={50} />}
          </Typography>
        }
        secondary={
          <Typography color='textSecondary' style={{ fontSize: '0.8rem' }}>
            {companyKey || <Skeleton width={50} />}
          </Typography>
        }
      />
      <ListItemText
        disableTypography
        primary={
          <div style={{ display: 'inline' }}>
            <Typography
              variant='subtitle1'
              component='span'
              style={{ display: 'inline' }}
            >
              {name || <Skeleton width={120} />}
            </Typography>
            {isAdminOrHigher && loading && (
              <CircularProgress
                style={{ width: '20px', height: '20px', marginLeft: '20px' }}
              />
            )}
            {warnings.length > 0 && (
              <Tooltip
                classes={{
                  popper: classes.htmlPopper,
                  tooltip: classes.htmlTooltip,
                }}
                title={
                  <>
                    <ProjectNotificationsWidget
                      classes={classes}
                      projectKey={projectKey}
                    />
                  </>
                }
              >
                <NotificationImportant
                  style={{
                    width: '20px',
                    height: '20px',
                    marginLeft: '10px',
                    verticalAlign: 'sub',
                    color: '#e49601',
                  }}
                />
              </Tooltip>
            )}
          </div>
        }
        secondary={<Secondary {...project} />}
        inset
      />
      <ListItemSecondaryAction>
        <Mutation
          mutation={isFavourite ? UNFAVOURITE_PROJECT : FAVOURITE_PROJECT}
          refetchQueries={(x) => [
            {
              query: GET_FAV_PROJECTS,
            },
            {
              query: GET_PROJECTS,
            },
          ]}
        >
          {(favouriteProject) => (
            <IconButton
              aria-label='Add to favorites'
              color={isFavourite ? 'secondary' : 'default'}
              onClick={(x) => favouriteProject({ variables: { id } })}
            >
              <Star />
            </IconButton>
          )}
        </Mutation>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export default withStyles(styles)(withRouter(ProjectListItem));
